import { Checkbox, FormLabel, OptionType, SearchableSelect, Textinput } from "shared";
import {
    barcodeTypeLabels,
    BarcodeTypes,
    ITemplateBarcodeComponent,
    ITemplateComponent
} from "entities/template";
import React, { useMemo } from "react";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { VisibilityField } from "../../form_fields/visibility_field/visibility_field";
import { LabelField } from "../../form_fields/label_field/label_field";
import { TooltipField } from "../../form_fields/tooltip_field/tooltip_field";
import styles from "./component_form_barcode.module.scss";
import { useComponentValidateFunction } from "../../../../lib/hooks/useComponentValidateFunction";

interface IComponentFormBarcodeProps {
    component: ITemplateBarcodeComponent;
    enLanguageEnabled: boolean;
    componentList: ITemplateComponent[];
    onChange: (component: ITemplateComponent) => void;
    triggerValidate: number;
}

export const ComponentFormBarcode: React.FC<IComponentFormBarcodeProps> = React.memo(({
                                                                                          component,
                                                                                          enLanguageEnabled,
                                                                                          componentList,
                                                                                          triggerValidate,
                                                                                          onChange
                                                                                      }) => {
    const t = useComponentsTranslations();
    const { validateTag, requiredFieldValidation } = useComponentValidateFunction();

    const onFieldChange = (value: Partial<ITemplateBarcodeComponent>) => {
        onChange({
            ...component,
            ...value
        })
    }

    const barcodeTypeOptions: OptionType[] = useMemo(() => {
        return [
            {
                value: BarcodeTypes.EAN_13,
                label: barcodeTypeLabels[BarcodeTypes.EAN_13]
            },
            {
                value: BarcodeTypes.DATA_MATRIX,
                label: barcodeTypeLabels[BarcodeTypes.DATA_MATRIX]
            }]
    }, [])

    return <div className={styles.container}>
        <div className={styles.description}>{t("barcodeComponentDescription")}</div>
        <LabelField component={component}
                    enLanguageEnabled={enLanguageEnabled}
                    title={t("formLabel")}
                    name="label-dynamic-input"
                    onChange={onFieldChange}/>
        <FormLabel title={t("formTag")} required={true}>
            <Textinput size="m"
                       triggerValidate={triggerValidate}
                       validate={true}
                       validateFunc={validateTag}
                       value={component.name ? component.name : ""}
                       onChange={(e) => onFieldChange({ name: e.target.value })}
                       view="outline"/>
        </FormLabel>
        <FormLabel title={t("barcodeType")} required={true}>
            <SearchableSelect
                options={barcodeTypeOptions}
                optionValue={barcodeTypeOptions.find((o) => o.value === component.barcodeType)}
                onChange={(val) => {
                    onFieldChange({ barcodeType: val as BarcodeTypes })
                }}
                isMulti={false}
                isClearable={false}
                hideClearIcon={true}
                isSearchable={true}
            />
        </FormLabel>
        <FormLabel title={t("sizeRatio")} required={true}>
            <Textinput size="m"
                       triggerValidate={triggerValidate}
                       validate={true}
                       validateFunc={requiredFieldValidation}
                       value={component.ratio ? component.ratio : ""}
                       onChange={(e) => onFieldChange({ ratio: e.target.value })}
                       view="outline"/>
            <div className={styles.subLabel}>{t("sizeRatioDescription")}</div>
        </FormLabel>
        <VisibilityField
            component={component}
            componentList={componentList}
            onChange={onFieldChange}/>
        <TooltipField
            component={component}
            enLanguageEnabled={enLanguageEnabled}
            onChange={onFieldChange}
        />
        <Checkbox
            label={t("requiredField")}
            size="m"
            onChange={() => onFieldChange({ required: typeof component.required === "boolean" ? !component.required : false })}
            checked={typeof component.required === "boolean" ? component.required : true}
        />
        <Checkbox
            label={t("rowGroup")}
            size="m"
            onChange={() => onFieldChange({ isRowGroup: !component.isRowGroup })}
            checked={component.isRowGroup}
        />
    </div>;
});
